import { action, computed, makeObservable } from "mobx";
import { View } from "tamagui";
import type { Api } from "../../../core/apis";
import type { Stores } from "../../../core/stores";
import BaseModuleDataModel, {
  type BaseModuleModelData,
} from "../../core/BaseModuleDataModel";

export type TrackerHistoryItem = {
  userUid: string;
  type: string;
  amount: number;
  ts: number;
  comment: string;
};

export interface TrackerUserDataProps extends BaseModuleModelData {
  userUid: string;
  history: TrackerHistoryItem[];
}

export default class TrackerUserData extends BaseModuleDataModel<TrackerUserDataProps> {
  constructor(stores: Stores, api: Api, data: TrackerUserDataProps) {
    super(stores, api, data);

    Object.assign(this.data, {
      userUid: this.data.userUid ?? "-",
      history: this.data.history ?? [],
    });

    makeObservable(this);
  }

  @action async deleteEntry(entry: TrackerHistoryItem) {
    const entryIndex = this.data.history.findIndex((i) => i.ts === entry.ts);

    if (entryIndex !== -1) {
      this.data.history.splice(entryIndex, 1);
      await this.api.modulesDataApi.set(this);
    }
  }

  @action async addEntry(entry: TrackerHistoryItem) {
    this.data.history.push(entry);
    await this.api.modulesDataApi.set(this);
  }

  @computed get lastEntryComment(): string | undefined {
    const lastItem = this.data.history[this.data.history.length - 1];
    if (lastItem) {
      return lastItem.comment;
    }
    return undefined;
  }

  @action async updateEntry(entry: TrackerHistoryItem) {
    const entryIndex = this.data.history.findIndex((i) => i.ts === entry.ts);

    if (entryIndex !== -1) {
      this.data.history[entryIndex] = entry;
      await this.api.modulesDataApi.set(this);
    }
  }

  @computed
  get user() {
    return this.stores.usersStore.byId.get(this.data.userUid);
  }

  static is(
    module: BaseModuleDataModel<BaseModuleModelData>,
  ): module is TrackerUserData {
    return module instanceof TrackerUserData;
  }

  render() {
    return <View />;
  }
}
